import { type IDIALOGVERIFYTRANSACTIONPROPS, Loader } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	cleanRate,
	convertFxRate,
	FXCONVERSION,
	FXRATE,
	getNetworkFee,
	type NetworkType,
	showToast,
	type TokenType,
} from 'BreetHelpers';
import { useCurrentFxRate, useModalApi, useSavedWalletAddressApi } from 'BreetHooks';
import { selectAuthSettings, useAppSelector, useGetUserPreferenceQuery } from 'BreetRedux';
import { useCallback, useMemo } from 'react';

import type { ICryptoTransactionDetailsProps } from './CryptoTransactionDetails.helpers';
import Details from './CryptoTransactionDetailsView';

const CryptoTransactionDetails = ({
	formDataContextProps,
	walletInfoOptions,
	walletInfoData,
	currencySymbol,
	type,
}: ICryptoTransactionDetailsProps) => {
	const authSettings = useAppSelector(selectAuthSettings);

	const { openModalDirect } = useModalApi();

	const { getSavedWalletAddress } = useSavedWalletAddressApi();

	const { isLoading: userPreferenceLoading } = useGetUserPreferenceQuery(undefined, alwaysRefetchApiQueryOptions);

	const usdWithdrawalMeta = authSettings?.usdWithdrawalMeta;

	const {
		hookFormProps: { getValues },
	} = formDataContextProps;

	const formValues = getValues();

	const networkFee = useMemo(
		() => getNetworkFee(formValues.token as TokenType, formValues.network as NetworkType, usdWithdrawalMeta),
		[formValues.token, formValues.network, usdWithdrawalMeta]
	);

	const buyCryptoCurrentFxRate = useCurrentFxRate({ fxRate: FXRATE.BUY, conversionType: FXCONVERSION.SELLUSD });

	const rate = `${buyCryptoCurrentFxRate.symbol.from} ${buyCryptoCurrentFxRate.rate} / ${buyCryptoCurrentFxRate.symbol.to}`;

	const totalAmount = !buyCryptoCurrentFxRate.rate
		? '0'
		: String(convertFxRate(cleanRate(buyCryptoCurrentFxRate.rate), cleanRate(formValues.amount), 'to'));

	const receiveUSDAmount = useMemo(() => Number(totalAmount) - networkFee, [totalAmount, networkFee]);

	const receiveFiatAmount = useMemo(() => Number(formValues.amount) - networkFee, [formValues.amount, networkFee]);

	const receiveAmount = type === 'buy-crypto' ? receiveUSDAmount : receiveFiatAmount;

	const tokenAvatar = useMemo(
		() => walletInfoOptions.find((option) => option.token === formValues.token)?.avatar,
		[walletInfoOptions, formValues.token]
	);

	const networks = useMemo(
		() => (walletInfoData?.data && formValues.token ? walletInfoData.data[formValues.token as TokenType].networks : []),
		[walletInfoData?.data, formValues.token]
	);

	const networkAvatar = useMemo(
		() => networks.find((network) => network.network === formValues.network)?.avatar,
		[networks, formValues.network]
	);

	const walletId = useMemo(
		() => getSavedWalletAddress({ address: formValues.receiverAddress })?._id,
		[formValues.receiverAddress, getSavedWalletAddress]
	);

	const submitRequest = useCallback(() => {
		if (!walletId) {
			showToast('error', 'Something went wrong, please try again later');
			console.error('wallet id not found');
			return;
		}

		const metadata = {
			transactionType: type === 'buy-crypto' ? 'convertFiatToUSD' : 'withdrawUSD',
			convertFiatToUSDReqData: {
				amount: Number(formValues.amount),
				withdrawalAddressId: walletId,
			},
			withdrawUSDReqData: {
				amount: Number(formValues.amount),
				walletId,
			},
		} as IDIALOGVERIFYTRANSACTIONPROPS;

		openModalDirect({
			id: 'verifyTransaction',
			metadata,
			variant: 'right-sheet',
		});
	}, [formValues.amount, openModalDirect, type, walletId]);

	if (userPreferenceLoading) return <Loader />;

	return (
		<Details
			formValues={formValues}
			tokenAvatar={tokenAvatar}
			networkAvatar={networkAvatar}
			networkFee={networkFee}
			receiveAmount={receiveAmount}
			handleVerification={submitRequest}
			currencySymbol={currencySymbol}
			type={type}
			rate={rate}
			totalAmount={totalAmount}
		/>
	);
};

export default CryptoTransactionDetails;
